// components/StepModal.tsx
import { Box, Button, FormField, Input, Modal, Select, SpaceBetween, Textarea } from '@cloudscape-design/components';
import { useEffect, useState } from 'react';
import { stepTypeOptions, suggestStepType, type Step, type StepType } from '../models/Recipe';

type StepModalProps = {
  visible: boolean;
  initial?: Step | null; // if provided, we’re editing
  onDismiss: () => void;
  onSave: (step: Step) => void; // returns a fully-built Step
  title?: string; // override header if you want
};

const toNumberOrUndefined = (v: string) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};

export default function StepModal({ visible, initial, onDismiss, onSave, title = 'Add step' }: StepModalProps) {
  const [type, setType] = useState<{ label: string; value: StepType } | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [duration, setDuration] = useState('');
  const [temperature, setTemperature] = useState('');
  const [gravity, setGravity] = useState('');
  const [validate, setValidate] = useState(false);

  // hydrate when opened or when initial changes
  useEffect(() => {
    if (!visible) return;
    const match = stepTypeOptions.flatMap((group) => group.options).find((o) => o.value === initial?.type);

    setType(match ? { label: match.label, value: match.value as StepType } : null);

    setName(initial?.name ?? '');
    setDescription(initial?.description ?? '');
    setDuration(initial?.duration?.toString() ?? '');
    setTemperature(initial?.temperature?.toString() ?? '');
    setGravity(initial?.gravity?.toString() ?? '');
    setValidate(false);
  }, [visible, initial]);

  const handleSave = () => {
    setValidate(true);
    if (!name || !duration) return;

    const step: Step = {
      // keep type optional for legacy safety; add it here later if you add a select
      name,
      type: type?.value,

      description: description || undefined,
      duration: parseInt(duration, 10) || 0,
      temperature: toNumberOrUndefined(temperature),
      gravity: toNumberOrUndefined(gravity),
    };

    onSave(step);
  };

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header={title}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="m">
            <Button variant="normal" onClick={onDismiss}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave}>
              Save
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween direction="vertical" size="l">
        <FormField label="Name" errorText={validate && !name ? 'Required' : undefined}>
          <Input placeholder="Name" value={name} onChange={({ detail }) => setName(detail.value)} />
        </FormField>

        <FormField label="Description">
          <Textarea
            placeholder="Description"
            value={description}
            onChange={({ detail }) => setDescription(detail.value)}
          />
        </FormField>

        <FormField
          label="Step Type"
          info={
            <Button
              variant="inline-link"
              iconName="gen-ai"
              onClick={() => {
                const suggestedType = suggestStepType({ name, description });

                // find the matching option
                const match = stepTypeOptions.flatMap((group) => group.options).find((o) => o.value === suggestedType);
                if (match) setType(match as { label: string; value: StepType });
              }}
              ariaLabel="Suggest step type"
            >
              Suggest type
            </Button>
          }
        >
          <Select
            selectedOption={type}
            onChange={({ detail }) => setType(detail.selectedOption as { label: string; value: StepType })}
            options={stepTypeOptions}
            placeholder="Select step type"
            filteringType="auto"
          />
        </FormField>

        <FormField label="Duration (days)" errorText={validate && !duration ? 'Required' : undefined}>
          <Input placeholder="e.g. 7" value={duration} onChange={({ detail }) => setDuration(detail.value)} />
        </FormField>

        <FormField label="Temperature (°C)">
          <Input placeholder="e.g. 18" value={temperature} onChange={({ detail }) => setTemperature(detail.value)} />
        </FormField>

        <FormField label="Gravity (SG)">
          <Input placeholder="e.g. 1.010" value={gravity} onChange={({ detail }) => setGravity(detail.value)} />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
}
